/**
 * Sod Calculator engine — pure functions, no side effects.
 *
 * Turns a lawn area (sq ft) into a sod order (pallets / rolls / individual
 * pieces) with a standard cutting allowance, prices the material and a
 * professional install for the user's region, and compares the material cost
 * against seeding the same area. Region resolution reuses the site-wide
 * getRegionFromZipCode so the same ZIP maps to the same region everywhere.
 *
 * The math is ported 1:1 from the approved Claude Design prototype
 * ("Sod Calculator.dc.html"), with one deliberate upgrade: the prototype keyed
 * pricing off the first ZIP digit; here we resolve the canonical named region
 * and look up a multiplier, so region naming stays consistent site-wide.
 */

import { getRegionFromZipCode } from '@/data/regional-climate'

// --- Coverage constants (national conventions; tunable) ---------------------
/** A standard sod pallet covers ~450 sq ft. Matches existing site copy. */
export const PALLET_SQFT = 450
/** One 16x24 in slab ~= 2.67 sq ft. */
export const PIECE_SQFT = 2.67
/** One 2x5 ft roll = 10 sq ft. */
export const ROLL_SQFT = 10
/** 10% cutting allowance for edges, curves, and offcuts. */
export const WASTE = 1.1
/** Rough average pallet weight in tons (~2,200 lb). */
const TONS_PER_PALLET = 1.1

export type SodGrassId =
  | 'bermuda'
  | 'zoysia'
  | 'staug'
  | 'centipede'
  | 'fescue'
  | 'kbg'
  | 'notsure'

/** Price band a sod variety falls into. */
export type SodBand = 'warm' | 'cool' | 'premium' | 'blend'

export type SodUnit = 'pallets' | 'rolls' | 'pieces'

export interface SodGrassOption {
  id: SodGrassId
  label: string
  band: SodBand
}

/**
 * Single source of truth for the sod-type chips + band mapping. Band assignment
 * follows 2026 material-price research: Bermuda and centipede are the cheapest
 * ("warm"), fescue/bluegrass sit in the middle ("cool"), and Zoysia and
 * St. Augustine command a premium.
 */
export const SOD_GRASSES: SodGrassOption[] = [
  { id: 'bermuda', label: 'Bermuda', band: 'warm' },
  { id: 'zoysia', label: 'Zoysia', band: 'premium' },
  { id: 'staug', label: 'St. Augustine', band: 'premium' },
  { id: 'centipede', label: 'Centipede', band: 'warm' },
  { id: 'fescue', label: 'Tall Fescue', band: 'cool' },
  { id: 'kbg', label: 'Kentucky Bluegrass', band: 'cool' },
  { id: 'notsure', label: 'Not sure', band: 'blend' },
]

/**
 * $ per 1,000 sq ft national baseline; a region multiplier scales it.
 * Calibrated to 2026 pricing research (HomeGuide, HomeAdvisor, Angi, LawnStarter,
 * Sod Solutions): sod material $0.30-0.85/sq ft (Bermuda ~$0.50, Zoysia/St. Aug
 * ~$0.65), pro install labor + basic prep ~$0.40-0.90/sq ft (installed all-in
 * averages ~$1.65/sq ft), and grass seed material $0.08-0.18/sq ft.
 */
const PRICE = {
  warm: 500, // Bermuda, centipede: ~$0.50/sq ft
  cool: 520, // Fescue, bluegrass: ~$0.52/sq ft
  premium: 650, // Zoysia, St. Augustine: ~$0.65/sq ft
  blend: 550, // national average ~$0.55/sq ft
  laborPer1000: 800, // pro install labor + basic surface prep, ~$0.80/sq ft
  seedWarm: 100, // warm-season seed ~$0.10/sq ft
  seedCool: 140, // cool-season seed ~$0.14/sq ft
} as const

/**
 * Regional price multiplier keyed by the canonical region names returned by
 * getRegionFromZipCode. Unknown ZIPs fall back to 1.0 ("National average").
 */
const SOD_REGION_MULT: Record<string, number> = {
  Northeast: 1.18,
  'Mid-Atlantic': 1.05,
  Southeast: 0.9,
  'Great Lakes': 1.0,
  'Upper Midwest': 1.05,
  Central: 0.95,
  'South Central': 0.9,
  'Mountain West': 1.1,
  'West Coast': 1.25,
  'Pacific Northwest': 1.15,
  California: 1.25,
  'Desert Southwest': 1.1,
  Florida: 0.95,
  Hawaii: 1.35,
  Alaska: 1.35,
}

export interface SodInput {
  areaSqFt: number
  /** Defaults to 'notsure' (blended pricing, no timing verdict). */
  grassId?: SodGrassId
  zipCode?: string
  /** Injected "current date" for deterministic tests. Defaults to real now. */
  now?: Date
}

export interface SodCostRange {
  /** Midpoint estimate. */
  mid: number
  low: number
  high: number
  /** "$1,200 – $1,600" */
  label: string
}

export interface SodResult {
  grassId: SodGrassId
  grassLabel: string
  /** e.g. "Bermuda sod" or "Sod estimate" for 'notsure'. */
  grassShort: string
  band: SodBand

  region: string | null
  regionKnown: boolean
  /** "Southeast" or "National average". */
  regionLabel: string
  mult: number

  netAreaSqFt: number
  orderAreaSqFt: number
  /** Whole-percent waste allowance (10). */
  wastePct: number

  pallets: number
  rolls: number
  pieces: number
  /** Approx delivered weight in tons. */
  tons: number
  weightLabel: string

  material: SodCostRange
  installed: SodCostRange

  /** Sod material midpoint (the "DIY" number in the compare card). */
  sodMaterialMid: number
  sodMaterialLabel: string
  /** Seed cost for the same area. */
  seedMid: number
  seedLabel: string
  /** sodMaterialMid - seedMid (positive: sod costs more). */
  delta: number
  deltaNote: string

  /** True when today falls in the grass's rooting window (false for 'notsure'). */
  inSeason: boolean
  timingCaution: string

  prepSteps: string[]
}

// --- formatting helpers -----------------------------------------------------

/** Round to the nearest $100 above $10k, else nearest $10, and format. */
function money(v: number): string {
  const rounded = v >= 10000 ? Math.round(v / 100) * 100 : Math.round(v / 10) * 10
  return '$' + rounded.toLocaleString()
}

function costRange(mid: number): SodCostRange {
  const low = mid * 0.85
  const high = mid * 1.15
  return { mid, low, high, label: `${money(low)} – ${money(high)}` }
}

function getGrass(id: SodGrassId | undefined): SodGrassOption {
  return SOD_GRASSES.find((g) => g.id === id) ?? SOD_GRASSES[SOD_GRASSES.length - 1]
}

/**
 * Build the hero metric label for a given unit selection. Kept here so the UI
 * and any tests format units identically.
 */
export function formatSodUnit(result: SodResult, unit: SodUnit): { big: string; sub: string } {
  if (unit === 'pallets') {
    return {
      big: `${result.pallets} ${result.pallets === 1 ? 'pallet' : 'pallets'}`,
      sub: 'at ~450 sq ft per pallet, waste included',
    }
  }
  if (unit === 'rolls') {
    return {
      big: `${result.rolls.toLocaleString()} rolls`,
      sub: 'at 10 sq ft per roll (2×5 ft), waste included',
    }
  }
  return {
    big: `${result.pieces.toLocaleString()} pieces`,
    sub: '16×24 in slabs (~2.67 sq ft each), waste included',
  }
}

export function calculateSod(input: SodInput): SodResult {
  const g = getGrass(input.grassId)
  const area = Number.isFinite(input.areaSqFt) ? Math.max(0, input.areaSqFt) : 0
  const now = input.now ?? new Date()

  const region = input.zipCode ? getRegionFromZipCode(input.zipCode) : 'Unknown'
  const regionKnown = region !== 'Unknown'
  const mult = regionKnown ? SOD_REGION_MULT[region] ?? 1.0 : 1.0
  const regionLabel = regionKnown ? region : 'National average'

  const orderArea = Math.round(area * WASTE)
  const pallets = orderArea > 0 ? Math.ceil(orderArea / PALLET_SQFT) : 0
  const rolls = orderArea > 0 ? Math.ceil(orderArea / ROLL_SQFT) : 0
  const pieces = orderArea > 0 ? Math.ceil(orderArea / PIECE_SQFT) : 0
  const tons = pallets * TONS_PER_PALLET

  const sodRate = PRICE[g.band] * mult
  const matMid = (orderArea / 1000) * sodRate
  const labor = (orderArea / 1000) * PRICE.laborPer1000 * mult
  const instMid = matMid + labor

  const warm = g.band === 'warm' || g.band === 'premium'
  const seedRate = (warm ? PRICE.seedWarm : PRICE.seedCool) * mult
  const seedMid = area > 0 ? Math.max(30, (area / 1000) * seedRate) : 0
  const delta = matMid - seedMid
  const deltaNote =
    delta > 0
      ? `Sodding this lawn costs about ${money(delta)} more in materials than seeding it, and gives you a finished lawn the same day instead of in 6-8 weeks.`
      : delta < 0
        ? `For an area this small, the seed minimum costs about ${money(Math.abs(delta))} more than the calculated sod material. Check local minimum-order rules before buying.`
        : 'The calculated sod and seed material costs are about the same for this area.'

  // Season-aware timing. Warm-season sod roots best late spring–summer;
  // cool-season prefers early fall (spring the backup).
  const month = now.getMonth() + 1
  const inSeason =
    g.id === 'notsure'
      ? false
      : warm
        ? month >= 4 && month <= 9
        : (month >= 3 && month <= 5) || (month >= 8 && month <= 10)

  const timingCaution =
    g.id === 'notsure'
      ? 'Timing depends on the sod type. Warm-season sod roots best late spring through summer; cool-season sod prefers early fall. Pick a type above for a verdict.'
      : warm
        ? inSeason
          ? `Good news: ${g.label} sod is in its rooting season right now (late spring through summer). Order for delivery on install day; sod on the pallet is only good for 24-48 hours.`
          : `${g.label} sod roots best when laid late spring through summer. It can go down now, but expect slower knitting until soil warms.`
        : inSeason
          ? `Good news: ${g.label} sod is in a good rooting window right now. Order for delivery on install day; sod on the pallet is only good for 24-48 hours.`
          : `${g.label} sod roots best in early fall (spring is the backup). Laying it now works with irrigation, but the roots knit slower.`

  return {
    grassId: g.id,
    grassLabel: g.label,
    grassShort: g.id === 'notsure' ? 'Sod estimate' : `${g.label} sod`,
    band: g.band,

    region: regionKnown ? region : null,
    regionKnown,
    regionLabel,
    mult,

    netAreaSqFt: Math.round(area),
    orderAreaSqFt: orderArea,
    wastePct: Math.round((WASTE - 1) * 100),

    pallets,
    rolls,
    pieces,
    tons,
    weightLabel: tons >= 10 ? `${Math.round(tons)} tons` : `${tons.toFixed(1)} tons`,

    material: costRange(matMid),
    installed: costRange(instMid),

    sodMaterialMid: matMid,
    sodMaterialLabel: `${money(matMid)} DIY`,
    seedMid,
    seedLabel: money(seedMid),
    delta,
    deltaNote,

    inSeason,
    timingCaution,

    prepSteps: [
      'Kill and strip the old grass, then rough-grade 1 inch below walkways so the sod sits flush.',
      'Spread starter fertilizer and rake the soil smooth the day before delivery.',
      'Schedule delivery for install day. Sod is perishable: 24-48 hours on the pallet, less in summer.',
      'Water within 30 minutes of laying, then daily for 2 weeks. Check corners; they dry out first.',
    ],
  }
}
